import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Plant } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@entities/app/plant.entity';
import { AdviceResponseType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/advice-response-type.enum';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { ListRowComponent } from '../../../@commons/ui-components/list-row/list-row.component';
import { ListSectionComponent } from '../../../@commons/ui-components/list-row/list-section.component';
import { TagComponent, TagTone } from '../../../@commons/ui-components/tag/tag.component';
import { NgComponentOutlet } from '@angular/common';
import { LINE_ICONS } from '../../../@icons/line-icons';
import { ThreeDotsIconComponent } from '../../../@icons/three-dots-icon.component';
import { UndoSnackbarComponent } from '../../../@commons/ui-components/undo-snackbar/undo-snackbar.component';
import { UndoableList } from '../../../@commons/ui-components/undo-snackbar/undoable-list';
import { AdviceReplyComponent, AdviceReplyKind, AdviceReplyTarget } from '../../../@commons/@components/advice-reply/advice-reply.component';
import { AdviceReplyStore, SentReply } from '../../../@commons/@services/data/advice-reply.store';
import { AlertsStore } from '../../../@commons/@services/data/alerts.store';
import { EndCultureToastComponent, EndablePlant } from '../../../garden/@toasts/end-culture-toast/end-culture-toast.component';
import { HarvestLogToastComponent } from '../../../garden/@toasts/harvest-log-toast/harvest-log-toast.component';
import routesConstants from '../../../@commons/constants/routes.constants';
import { DayMessageComponent } from '../day-message/day-message.component';
import { DayTextPipe, renderDayText } from '../day-message/day-text.pipe';
import { SetupStepService } from '../first-launch/setup-step.service';
import { WillyChatService } from '../../../@commons/@services/front/willy-chat.service';
import { WILLY_SRC } from '../../../@commons/ui-components/empty-state/empty-state.component';
import { CALM_FAMILIES, DayChoice, DayMessage, DayText, DayTone, namesInSentence } from '../day-message/day-message.model';
import { MAX_OTHERS } from '../day-message/day-message.engine';

/** « Annuler » reste 6 secondes après une réponse (proposition A, règle 3). */
export const REPLY_UNDO_MS = 6000;

const TAG_TONES: Record<DayTone, TagTone> = {
  frost: 'cold',
  water: 'cold',
  wind: 'wind',
  go: 'go',
  calm: 'go',
  harvest: 'harvest',
  alert: 'stale',
};

/**
 * Le mot du jour et ses autres gestes (`vw-day-board`), avec les réponses.
 *
 * Il dessine le message principal (`vw-day-message`), puis ce que l'écran
 * projette avec `vwDayBoardBand` (la bande météo et lune), les deux gestes
 * suivants en lignes de 64 px et le lien « Cette quinzaine ». Répondre fait
 * partir le message tout de suite, et le suivant remonte : « C'est fait » et
 * « Déjà fait » partent au serveur (gardés sur l'appareil sans réseau) avec
 * « Annuler » 6 secondes ; « Plus là » finit la plante, « J'en ai cueilli »
 * note la récolte, « Actualiser » ouvre la fiche sur le choix de l'étape.
 * Un jour calme, une ligne de plus montre une question à poser à Willy.
 */
@Component({
  selector: 'vw-day-board',
  standalone: true,
  imports: [
    TranslatePipe, SharedModule, ListRowComponent, ListSectionComponent, TagComponent, NgComponentOutlet, ThreeDotsIconComponent, UndoSnackbarComponent,
    AdviceReplyComponent, EndCultureToastComponent, HarvestLogToastComponent, DayMessageComponent, DayTextPipe,
  ],
  templateUrl: './day-board.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DayBoardComponent extends UndoableList implements OnInit {
  @Input() choice: DayChoice | null = null;
  /** Ce qui attend dans « Cette quinzaine » en plus des messages : les gestes du jardin. */
  @Input() tasksCount = 0;

  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly replies = inject(AdviceReplyStore);
  private readonly alerts = inject(AlertsStore);
  private readonly setupStep = inject(SetupStepService);
  private readonly willyChat = inject(WillyChatService);

  readonly undoMs = REPLY_UNDO_MS;
  readonly icons = LINE_ICONS;
  readonly willyImage = WILLY_SRC.hi;
  /** Message dont la feuille de réponse est ouverte. */
  replying: DayMessage | null = null;
  replyTarget: AdviceReplyTarget | null = null;
  /** « Plus là » : les plantes à finir. */
  ending: { message: DayMessage; plants: EndablePlant[] } | null = null;
  /** « J'en ai cueilli » : la plante dont on note la récolte. */
  picking: { message: DayMessage; plant: Plant } | null = null;
  undoText = '';
  /** Messages réglés sans retour possible (plante finie, récolte notée). */
  private readonly settled = new Set<string>();

  /** Les réponses données hors ligne partent dès que l'accueil s'ouvre avec du réseau. */
  ngOnInit(): void {
    void this.replies.flush();
  }

  /** Les messages encore à l'écran, dans l'ordre du moteur : répondre fait remonter le suivant. */
  get visible(): DayMessage[] {
    const all = this.choice ? [this.choice.head, ...this.choice.others, ...this.choice.rest] : [];
    return all.filter((m): m is DayMessage => !!m && !this.checked.has(m.key) && !this.gone.has(m.key) && !this.settled.has(m.key));
  }

  get head(): DayMessage | null {
    return this.visible[0] ?? null;
  }

  get others(): DayMessage[] {
    return this.visible.slice(1, 1 + MAX_OTHERS);
  }

  /** Ce qui reste pour la quinzaine : les messages au-delà des trois, et les gestes du jardin. */
  get fortnight(): number {
    return Math.max(0, this.visible.length - 1 - MAX_OTHERS) + this.tasksCount;
  }

  /** La question d'exemple à Willy, tant que le message en tête est un jour calme. */
  get willy(): DayText | null {
    const head = this.head;
    return head && CALM_FAMILIES.includes(head.family) ? (this.choice?.willy ?? null) : null;
  }

  /** La question s'ouvre dans la discussion, déjà écrite. */
  askWilly(question: DayText): void {
    this.willyChat.openChat(renderDayText(this.translate, question));
  }

  /** En ligne, l'impératif court, sans point final comme dans la maquette (« Récolte tes radis »). */
  rowTitle(message: DayMessage): string {
    return renderDayText(this.translate, message.rowTitle).replace(/\.$/, '');
  }

  /** Un message qui mène à un écran sans parler d'une plante du jardin : rien à répondre, la ligne est un lien. */
  leadsAway(message: DayMessage): boolean {
    return message.button?.action === 'OPEN' && !!message.link && !message.plants.some((p) => !!p.uid);
  }

  tagTone(message: DayMessage): TagTone {
    return TAG_TONES[message.tone] ?? 'go';
  }

  /** Le bouton du message : répondre, noter la récolte, ou ouvrir l'écran du geste. */
  onAct(message: DayMessage): void {
    switch (message.button?.action) {
      case 'PICKED':
        this.pick(message);
        break;
      case 'OPEN':
        if (message.link) void this.router.navigateByUrl(message.link);
        break;
      case 'ALERTS':
        this.setupStep.open('alerts');
        break;
      case 'INSTALL':
        this.setupStep.open('install');
        break;
      default:
        void this.answer(message, AdviceResponseType.DONE);
    }
  }

  openReply(message: DayMessage): void {
    const first = message.plants[0];
    this.replying = message;
    this.replyTarget = {
      // Le titre de la feuille, sans point final comme dans la maquette.
      title: renderDayText(this.translate, message.title).replace(/\.$/, ''),
      subtitle: first?.location ?? null,
      imageUrl: first?.imageUrl ?? null,
      replies: this.repliesFor(message),
      gesture: message.button?.key ?? null,
      form: message.title.form ?? null,
    };
  }

  closeReply(): void {
    this.replying = null;
    this.replyTarget = null;
  }

  onReply(kind: AdviceReplyKind): void {
    const message = this.replying;
    this.closeReply();
    if (!message) return;
    switch (kind) {
      case 'DONE':
      case 'ALREADY_DONE':
        void this.answer(message, kind === 'DONE' ? AdviceResponseType.DONE : AdviceResponseType.ALREADY_DONE);
        break;
      case 'GONE':
        this.ending = {
          message,
          plants: this.plantsOf(message).map((p) => p.plant ?? { uid: p.uid!, cultivationVariant: { name: p.name } }),
        };
        break;
      case 'EDIT':
        void this.router.navigate(['/garden/plants', this.plantsOf(message)[0].uid], { queryParams: { action: 'choose-step' } });
        break;
      case 'PICKED':
        this.pick(message);
        break;
    }
  }

  /** La plante est finie : ses messages s'en vont. */
  onEnded(): void {
    const ended = new Set(this.ending?.plants.map((p) => p.uid));
    this.ending = null;
    for (const m of this.visible) {
      if (m.plants.length && m.plants.every((p) => p.uid && ended.has(p.uid))) this.settled.add(m.key);
    }
  }

  /** La récolte est notée : le message part, et la réponse « C'est fait » le dit au serveur. */
  onPicked(): void {
    const picking = this.picking;
    this.picking = null;
    if (!picking) return;
    this.settled.add(picking.message.key);
    const plantUid = (picking.plant as { uid?: string }).uid;
    this.replies
      .reply({ plantUid, triggerCode: picking.message.code, response: AdviceResponseType.DONE })
      .catch((e) => console.warn('DayBoard: failed to send the harvest answer', e));
  }

  openFortnight(): void {
    void this.router.navigate([routesConstants.AGENDA_TASKS]);
  }

  /**
   * « C'est fait », « Déjà fait » : le message part tout de suite, la réponse
   * aussi (une par plante nommée), « Annuler » la reprend pendant 6 secondes.
   */
  private async answer(message: DayMessage, response: AdviceResponseType): Promise<void> {
    this.settle();
    this.check(message.key);
    const uids = this.plantsOf(message).map((p) => p.uid!);
    const requests = (uids.length ? uids : [undefined]).map((plantUid) => ({ plantUid, triggerCode: message.code, response }));
    let sent: SentReply[] = [];
    const sending = Promise.all(requests.map((request) => this.replies.reply(request))).then((done) => (sent = done));
    // « Pommes de terre couvertes, c'est noté. » quand le geste s'accorde, sinon « Pomme de terre, c'est noté. ».
    const noted: DayText = { key: 'home.day.noted', params: { name: this.nameOf(message) } };
    this.undoText = renderDayText(this.translate, message.noted ? { ...message.noted, fallback: noted } : noted);
    this.pendingUndo = {
      key: message.key,
      message: 'home.day.noted',
      // L'alerte ne passe « lue » qu'une fois la réponse acquise, pas pendant « Annuler ».
      commit: async () => {
        if (message.alertUid) await this.alerts.markRead(message.alertUid);
      },
      revert: async () => {
        await sending.catch(() => undefined);
        await Promise.all(sent.map((s) => this.replies.cancel(s)));
      },
    };
    try {
      await sending;
    } catch (e) {
      // Refusée par le serveur : le message revient, rien n'est noté.
      console.warn('DayBoard: failed to send the answer', e);
      this.restore(message.key);
    }
  }

  private pick(message: DayMessage): void {
    const plant = this.plantsOf(message)[0];
    if (!plant) return;
    this.picking = { message, plant: (plant.plant ?? { uid: plant.uid }) as unknown as Plant };
  }

  private plantsOf(message: DayMessage) {
    return message.plants.filter((p) => !!p.uid);
  }

  /** Les réponses qui ont du sens : la plante d'abord, et le geste quand il y en a un. */
  private repliesFor(message: DayMessage): AdviceReplyKind[] {
    const plants = this.plantsOf(message);
    return [
      'DONE',
      'ALREADY_DONE',
      ...(plants.length === 1 && (message.family === 'recolte' || message.button?.action === 'PICKED') ? ['PICKED' as const] : []),
      ...(plants.length ? ['GONE' as const] : []),
      ...(plants.length === 1 ? ['EDIT' as const] : []),
    ];
  }

  private nameOf(message: DayMessage): string {
    const names = message.plants.map((p) => p.name);
    if (names.length) return namesInSentence(names, this.translate.getCurrentLang() || 'fr').replace(/^./, (c) => c.toLocaleUpperCase());
    return renderDayText(this.translate, message.shortTitle).replace(/\.$/, '');
  }
}
